import { useMemo } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import useMutationApi from 'src/api/useMutationApi';
import useCoreEnums, {
  CoreEnumOptionDTO,
} from 'src/api/core/core-enums/useCoreEnums';
import { UpdateCoreEnumData } from 'src/api/core/core-enums/useUpdateCoreEnum';
import { CoreEnumResponse } from 'src/api/core/core-enums/useCreateCoreEnum';
import useGetApi from '@/api/useGetApi';
import config from 'src/config';
import ApiResponse from '@/api/ApiResponse';
import ApiError from '@/api/ApiError';
import {
  REBATES_SYSTEM_ENUMS,
  COMMUNICATIONS_SYSTEM_ENUMS,
} from '@erp/shared-types';
import { buildPosthogHeaders } from 'src/api/posthogHeaders';

export interface CoreEnumOptionAssociation {
  id: string;
  coreEnumOptionId: string;
  entityType: string;
  entityId: string;
  createdAt: string;
  updatedAt: string;
  coreEnumOption?: {
    id: string;
    name: string | null;
    description: string | null;
    sortOrder: number;
    enumId: string;
  };
}

interface BulkUpdateData {
  entityType: string;
  entityId: string;
  enumOptionIds: string[];
}

// Hook to fetch associations for a specific entity
export const useEnumOptionAssociations = (
  entityType: string | undefined,
  entityId: string | undefined,
  enabled = true,
) => {
  return useGetApi<CoreEnumOptionAssociation[]>({
    endpoint: `core/enum-option-associations?entityType=${entityType}&entityId=${entityId}`,
    options: {
      enabled: enabled && !!entityType && !!entityId,
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
    },
  });
};

// Hook to bulk fetch associations for multiple entities
export const useBulkEnumOptionAssociations = (
  entities: Array<{ entityType: string; entityId: string }> | undefined,
  enabled = true,
) => {
  // Create a stable key for caching based on sorted entity identifiers
  const queryKey =
    entities && entities.length > 0
      ? [
          'core/enum-option-associations/bulk-list',
          entities
            .map((e) => `${e.entityType}:${e.entityId}`)
            .sort()
            .join(','),
        ]
      : ['core/enum-option-associations/bulk-list'];

  return useQuery<CoreEnumOptionAssociation[] | undefined, ApiError>({
    queryKey,
    queryFn: async () => {
      if (!entities || entities.length === 0) {
        return undefined;
      }

      const response = await fetch(
        `${config.apiBaseUrl}/core/enum-option-associations/bulk-list`,
        {
          method: 'POST',
          headers: buildPosthogHeaders({
            'Content-Type': 'application/json',
          }),
          credentials: 'include',
          body: JSON.stringify({ entities }),
        },
      );

      if (!response.ok) {
        const errorResponse: ApiError = await response.json();
        throw new ApiError(
          errorResponse.status || response.status,
          errorResponse.message,
          errorResponse.erpSystemName,
          errorResponse.erpErrorDetails,
        );
      }

      const data: ApiResponse<CoreEnumOptionAssociation[]> =
        await response.json();
      return data.data;
    },
    enabled: enabled && !!entities && entities.length > 0,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

// Hook to bulk update associations (replaces all tags for an entity)
export const useBulkUpdateEnumOptionAssociations = (
  entityType: string,
  entityId: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient();

  return useMutationApi<BulkUpdateData, CoreEnumOptionAssociation[]>({
    endpoint: 'core/enum-option-associations/bulk',
    method: 'PUT',
    successMessage: 'Tags updated successfully',
    errorMessage: 'Failed to update tags',
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['enumOptionAssociations', entityType, entityId],
        });
        onSuccess?.();
      },
    },
  });
};

interface AddEnumOptionAssociationRequest {
  entityType: string;
  entityId: string;
  enumOptionIds: string[];
}

interface AddEnumOptionAssociationResponse {
  enumOptionAssociation: CoreEnumOptionAssociation;
}

export const useUpsertEnumOptionAssociationToEntity = () => {
  return useMutationApi<
    AddEnumOptionAssociationRequest,
    AddEnumOptionAssociationResponse
  >({
    endpoint: 'core/enum-option-associations/bulk',
    method: 'PUT',
  });
};

// Hook specifically for managing rebate agreement tags

export const useRebateAgreementTags = (agreementId: string | undefined) => {
  const queryClient = useQueryClient();

  /** 🧩 Fetch current tags for the agreement */
  const {
    data: currentTags = [],
    isLoading: tagsLoading,
    refetch,
  } = useEnumOptionAssociations('rebate_agreement', agreementId, !!agreementId);

  /** 🧩 Fetch enum definition ("Rebate Agreement Tags") */
  const { data: enumsResponse, isLoading: enumsLoading } = useCoreEnums(
    {
      name: REBATES_SYSTEM_ENUMS.AGREEMENT_TAGS.name,
      includeOptions: true,
    },
    {
      enabled: !!agreementId,
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
    },
  );

  const availableTags = enumsResponse?.edges?.[0]?.options ?? [];
  const enumId = enumsResponse?.edges?.[0]?.id;

  /** 🧩 Bulk update associations */
  const { mutate: updateTags, isPending: isUpdating } = useMutationApi<
    BulkUpdateData,
    CoreEnumOptionAssociation[]
  >({
    endpoint: 'core/enum-option-associations/bulk',
    method: 'PUT',
    options: {
      onSuccess: () => {
        // Invalidate individual query
        queryClient.invalidateQueries({
          queryKey: ['enumOptionAssociations', 'rebate_agreement', agreementId],
        });
        // Invalidate bulk query cache to ensure updates are reflected
        queryClient.invalidateQueries({
          queryKey: ['core/enum-option-associations/bulk-list'],
        });
        refetch();
      },
    },
  });

  /** 🧩 Helpers for tag add/remove */
  const handleUpdateTags = (enumOptionIds: string[]) => {
    updateTags({
      entityType: 'rebate_agreement',
      entityId: agreementId || '',
      enumOptionIds,
    });
  };

  const handleAddTag = (tagId: string) => {
    const currentTagIds = currentTags.map((t) => t.coreEnumOptionId);
    if (!currentTagIds.includes(tagId)) {
      handleUpdateTags([...currentTagIds, tagId]);
    }
  };

  const handleRemoveTag = (tagId: string) => {
    const newTagIds = currentTags
      .map((t) => t.coreEnumOptionId)
      .filter((id) => id !== tagId);
    handleUpdateTags(newTagIds);
  };

  /** 🧩 Derive current tag IDs */
  const currentTagIds = useMemo(
    () => new Set(currentTags.map((t) => t.coreEnumOptionId)),
    [currentTags],
  );

  return {
    currentTags,
    availableTags,
    enumId,
    isLoading: tagsLoading || enumsLoading,
    isUpdating,
    handleAddTag,
    handleRemoveTag,
    currentTagIds,
    refetch,
  };
};

// Hook specifically for managing rebate rule tags
export const useRebateRuleTags = (ruleId: string | undefined) => {
  const queryClient = useQueryClient();

  /** 🧩 Fetch current tags for the rule */
  const {
    data: currentTags = [],
    isLoading: tagsLoading,
    refetch,
  } = useEnumOptionAssociations('rebate_rule', ruleId, !!ruleId);

  /** 🧩 Fetch enum definition ("Rebate Rule Tags") */
  const { data: enumsResponse, isLoading: enumsLoading } = useCoreEnums(
    {
      name: REBATES_SYSTEM_ENUMS.RULE_TAGS.name,
      includeOptions: true,
    },
    {
      enabled: !!ruleId,
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
    },
  );

  const availableTags = enumsResponse?.edges?.[0]?.options ?? [];
  const enumId = enumsResponse?.edges?.[0]?.id;

  /** 🧩 Bulk update associations */
  const { mutate: updateTags, isPending: isUpdating } = useMutationApi<
    BulkUpdateData,
    CoreEnumOptionAssociation[]
  >({
    endpoint: 'core/enum-option-associations/bulk',
    method: 'PUT',
    options: {
      onSuccess: () => {
        // Invalidate individual query
        queryClient.invalidateQueries({
          queryKey: ['enumOptionAssociations', 'rebate_rule', ruleId],
        });
        // Invalidate bulk query cache to ensure updates are reflected
        queryClient.invalidateQueries({
          queryKey: ['core/enum-option-associations/bulk-list'],
        });
      },
    },
  });

  /** 🧩 Helpers for tag add/remove */
  const handleUpdateTags = (enumOptionIds: string[]) => {
    updateTags({
      entityType: 'rebate_rule',
      entityId: ruleId || '',
      enumOptionIds,
    });
  };

  const handleAddTag = (tagId: string) => {
    const currentTagIds = currentTags.map((t) => t.coreEnumOptionId);
    if (!currentTagIds.includes(tagId)) {
      handleUpdateTags([...currentTagIds, tagId]);
    }
  };

  const handleRemoveTag = (tagId: string) => {
    const newTagIds = currentTags
      .map((t) => t.coreEnumOptionId)
      .filter((id) => id !== tagId);
    handleUpdateTags(newTagIds);
  };

  /** 🧩 Derive current tag IDs */
  const currentTagIds = useMemo(
    () => new Set(currentTags.map((t) => t.coreEnumOptionId)),
    [currentTags],
  );

  return {
    currentTags,
    availableTags,
    enumId,
    isLoading: tagsLoading || enumsLoading,
    isUpdating,
    handleAddTag,
    handleRemoveTag,
    currentTagIds,
    refetch,
  };
};

// Hook specifically for managing supplier tags
export const useSupplierTags = (supplierId: string | undefined) => {
  const queryClient = useQueryClient();

  /** 🧩 Fetch current tags for the supplier */
  const {
    data: currentTags = [],
    isLoading: tagsLoading,
    refetch,
  } = useEnumOptionAssociations('supplier', supplierId, !!supplierId);

  /** 🧩 Fetch enum definition ("Supplier Tags") */
  const { data: enumsResponse, isLoading: enumsLoading } = useCoreEnums(
    {
      name: REBATES_SYSTEM_ENUMS.SUPPLIER_TAGS.name,
      includeOptions: true,
    },
    {
      enabled: !!supplierId,
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
    },
  );

  const availableTags = enumsResponse?.edges?.[0]?.options ?? [];
  const enumId = enumsResponse?.edges?.[0]?.id;

  /** 🧩 Bulk update associations */
  const { mutate: updateTags, isPending: isUpdating } = useMutationApi<
    BulkUpdateData,
    CoreEnumOptionAssociation[]
  >({
    endpoint: 'core/enum-option-associations/bulk',
    method: 'PUT',
    options: {
      onSuccess: () => {
        // Invalidate individual query
        queryClient.invalidateQueries({
          queryKey: ['enumOptionAssociations', 'supplier', supplierId],
        });
        // Invalidate bulk query cache to ensure updates are reflected
        queryClient.invalidateQueries({
          queryKey: ['core/enum-option-associations/bulk-list'],
        });
        refetch();
      },
    },
  });

  /** 🧩 Helpers for tag add/remove */
  const handleUpdateTags = (enumOptionIds: string[]) => {
    updateTags({
      entityType: 'supplier',
      entityId: supplierId || '',
      enumOptionIds,
    });
  };

  const handleAddTag = (tagId: string) => {
    const currentTagIds = currentTags.map((t) => t.coreEnumOptionId);
    if (!currentTagIds.includes(tagId)) {
      handleUpdateTags([...currentTagIds, tagId]);
    }
  };

  const handleRemoveTag = (tagId: string) => {
    const newTagIds = currentTags
      .map((t) => t.coreEnumOptionId)
      .filter((id) => id !== tagId);
    handleUpdateTags(newTagIds);
  };

  /** 🧩 Derive current tag IDs */
  const currentTagIds = useMemo(
    () => new Set(currentTags.map((t) => t.coreEnumOptionId)),
    [currentTags],
  );

  return {
    currentTags,
    availableTags,
    enumId,
    isLoading: tagsLoading || enumsLoading,
    isUpdating,
    handleAddTag,
    handleRemoveTag,
    currentTagIds,
    refetch,
  };
};

// Hook to manage the "Contact Tags" vocabulary (the enum options themselves),
// independent of any specific contact. Exposes the available options plus a
// `createTag` that adds a brand-new option to the org's Contact Tags enum.
// Shared by the per-contact tag hook and by add/edit forms that need to tag a
// contact that doesn't exist yet.
export const useContactTagOptions = () => {
  const queryClient = useQueryClient();

  const { data: enumsResponse, isLoading } = useCoreEnums(
    {
      name: COMMUNICATIONS_SYSTEM_ENUMS.CONTACT_TAGS.name,
      includeOptions: true,
    },
    {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
    },
  );

  const enumEdge = enumsResponse?.edges?.[0];
  const enumId = enumEdge?.id;
  const availableTags = enumEdge?.options ?? [];
  const areOptionsLocked = enumEdge?.areOptionsLocked ?? false;

  const { mutateAsync: updateEnum, isLoading: isCreatingTag } = useMutationApi<
    UpdateCoreEnumData,
    CoreEnumResponse
  >({
    endpoint: `core/core-enums/${enumId ?? ''}`,
    method: 'PUT',
    showSuccessToast: false,
    errorMessage: 'Failed to create tag',
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['core/core-enums'] });
      },
    },
  });

  const createTag = async (
    rawName: string,
  ): Promise<CoreEnumOptionDTO | undefined> => {
    const name = rawName.trim();
    if (!name || !enumId) return undefined;

    // Reuse an existing option if the name already exists (case-insensitive)
    // rather than creating a duplicate.
    const existing = availableTags.find(
      (option) =>
        (option.name ?? '').trim().toLowerCase() === name.toLowerCase(),
    );
    if (existing) return existing;

    // The update endpoint replaces the enum's entire option set, so resend
    // every existing option (with its id, to preserve it and its tag
    // associations) alongside the new one.
    const options = [
      ...availableTags
        .filter((option): option is CoreEnumOptionDTO & { name: string } =>
          Boolean(option.name),
        )
        .map((option) => ({
          id: option.id,
          name: option.name,
          description: option.description ?? undefined,
          sortOrder: option.sortOrder,
        })),
      { name, sortOrder: availableTags.length },
    ];

    const response = await updateEnum({ options });
    const created = response.data?.options?.find(
      (option) =>
        (option.name ?? '').trim().toLowerCase() === name.toLowerCase(),
    );
    if (!created) return undefined;

    return {
      id: created.id,
      name: created.name,
      description: created.description,
      sortOrder: created.sortOrder,
      originalId: null,
      originalSystem: null,
      originalEntity: null,
    };
  };

  return {
    availableTags,
    enumId,
    areOptionsLocked,
    isLoading,
    createTag,
    isCreatingTag,
  };
};

// Hook specifically for managing a single contact's tags (associations).
export const useContactTags = (contactId: string | undefined) => {
  const queryClient = useQueryClient();

  const {
    availableTags,
    enumId,
    areOptionsLocked,
    isLoading: optionsLoading,
    createTag,
    isCreatingTag,
  } = useContactTagOptions();

  const {
    data: currentTags = [],
    isLoading: tagsLoading,
    refetch,
  } = useEnumOptionAssociations('contact', contactId, !!contactId);

  const { mutate: updateTags, isPending: isUpdating } = useMutationApi<
    BulkUpdateData,
    CoreEnumOptionAssociation[]
  >({
    endpoint: 'core/enum-option-associations/bulk',
    method: 'PUT',
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['enumOptionAssociations', 'contact', contactId],
        });
        queryClient.invalidateQueries({
          queryKey: ['core/enum-option-associations/bulk-list'],
        });
        refetch();
      },
    },
  });

  const handleUpdateTags = (enumOptionIds: string[]) => {
    if (!contactId) return;
    updateTags({
      entityType: 'contact',
      entityId: contactId,
      enumOptionIds,
    });
  };

  const handleAddTag = (tagId: string) => {
    const currentTagIds = currentTags.map((t) => t.coreEnumOptionId);
    if (!currentTagIds.includes(tagId)) {
      handleUpdateTags([...currentTagIds, tagId]);
    }
  };

  const handleRemoveTag = (tagId: string) => {
    const newTagIds = currentTags
      .map((t) => t.coreEnumOptionId)
      .filter((id) => id !== tagId);
    handleUpdateTags(newTagIds);
  };

  // Create a new tag option in the enum, then immediately apply it to this
  // contact.
  const handleCreateTag = async (name: string) => {
    const created = await createTag(name);
    if (!created) return;
    const currentTagIds = currentTags.map((t) => t.coreEnumOptionId);
    if (!currentTagIds.includes(created.id)) {
      handleUpdateTags([...currentTagIds, created.id]);
    }
  };

  const currentTagIds = useMemo(
    () => new Set(currentTags.map((t) => t.coreEnumOptionId)),
    [currentTags],
  );

  return {
    currentTags,
    availableTags,
    enumId,
    areOptionsLocked,
    isLoading: tagsLoading || optionsLoading,
    isUpdating,
    isCreatingTag,
    handleAddTag,
    handleRemoveTag,
    handleCreateTag,
    currentTagIds,
    refetch,
  };
};
